import ky, { type BeforeRequestHook } from "ky";

import { CONSTANTS } from "@/config/constants";
import { useGlobalStore } from "@/stores/global";
import { ErrorResponse } from "@/types/pagination";

type GetTokenFn = () => Promise<string | null>;

let tokenGetter: GetTokenFn | null = null;

export const setTokenGetter = (fn: GetTokenFn) => {
  tokenGetter = fn;
};

const authHook: BeforeRequestHook = async (request) => {
  if (!tokenGetter) return;
  const token = await tokenGetter();
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }
};

const workspaceHook: BeforeRequestHook = (request) => {
  const { activeWorkspaceId } = useGlobalStore.getState();
  if (activeWorkspaceId) {
    request.headers.set("x-workspace-id", activeWorkspaceId);
  }
};

export const api = ky.create({
  prefixUrl: `${CONSTANTS.BACKEND_URL}/v1`,
  headers: {
    "x-tenant-id": CONSTANTS.APP_SLUG,
  },
  timeout: 1200000,
  hooks: {
    beforeRequest: [authHook, workspaceHook],
    beforeError: [
      async (error) => {
        const { response } = error;
        const isUnprocessableEntity = response.status === 422;

        try {
          const body = (await response.clone().json()) as ErrorResponse;

          if (isUnprocessableEntity) {
            const message =
              typeof body.message === "string"
                ? body.message
                : Object.values(body.message).join(", ");
            error.message = message;
          } else {
            error.message = body.message as string;
          }
        } catch {
          // Could not parse error body, keep original message
        }

        return error;
      },
    ],
  },
});
